import isEmpty from 'lodash/isEmpty';

import {ChartType, ChartVizType} from 'common/report/constants';
import {
  CommonToFunnelChartTypeMap,
  FunnelBookmark,
  FunnelsAggregateType,
  TrendChartType,
  TrendUnit,
} from 'common/report/funnels/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {getSortParamsIfWidgetTypeChange} from 'common/report/funnels/util';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {getDefaultTrendUnit, UNITS} from 'common/util/date';
import {getMergedBookmarkEventAndCohortFilters} from 'common/report/util/bookmark-overrides';

export function getBookmarkParamsWithChartType(
  bookmarkParams: FunnelBookmark,
  chartTypeOverride: ChartType,
): FunnelBookmark {
  if (!CommonToFunnelChartTypeMap[chartTypeOverride]) {
    throw new Error(`unsupported chart type: ${chartTypeOverride}`);
  }

  const funnelChartType = CommonToFunnelChartTypeMap[chartTypeOverride];
  let params: FunnelBookmark = {
    ...bookmarkParams,
    chart_type: funnelChartType,
    ...(chartTypeOverride === ChartType.FunnelStepsMetric ? {stepsVizType: ChartVizType.Metric} : {}),
    ...(chartTypeOverride === ChartType.FunnelSteps ? {stepsVizType: ChartVizType.Steps} : {}),

    ...(chartTypeOverride === ChartType.FunnelFrequencyLine ? {frequencyVizType: ChartVizType.Line} : {}),
    ...(chartTypeOverride === ChartType.FunnelFrequencyBar ? {frequencyVizType: ChartVizType.Bar} : {}),

    ...(chartTypeOverride === ChartType.FunnelTTCLine ? {ttcVizType: ChartVizType.Line} : {}),
    ...(chartTypeOverride === ChartType.FunnelTTCBar ? {ttcVizType: ChartVizType.Bar} : {}),
    ...(chartTypeOverride === ChartType.FunnelMedianTTC
      ? {ttcVizType: ChartVizType.Metric, ttcAggregateType: FunnelsAggregateType.Median}
      : {}),
  };

  const sortParams = getSortParamsIfWidgetTypeChange({
    oldBookmarkParams: bookmarkParams,
    newBookmarkParams: params,
  });

  if (!isEmpty(sortParams)) {
    params = {...params, ...sortParams};
  }

  return params;
}

export function getDefaultFunnelTrendUnit(dateRange: DateRange): TrendUnit {
  const filterUnit = getDefaultTrendUnit(dateRange.getIntervalDays());
  // funnels doesn't support hour unit
  // TODO(jakub): it does, maybe we don't allow it as default for perf reasons?
  const supportedFilterUnit = filterUnit === UNITS.hour ? UNITS.day : filterUnit;

  return supportedFilterUnit as TrendUnit;
}

export function getBookmarkParamsWithDateRange(
  bookmarkParams: FunnelBookmark,
  dateRangeOverride: DateRange,
): FunnelBookmark {
  // some bookmarks don't have a date_range
  const bookmarkDateRange = bookmarkParams.date_range && DateRange.fromBookmarkFormat(bookmarkParams.date_range);

  if (bookmarkDateRange?.equals(dateRangeOverride)) {
    return bookmarkParams;
  }

  const trendUnit = getDefaultFunnelTrendUnit(dateRangeOverride);
  return {
    ...bookmarkParams,
    date_range: dateRangeOverride.getBookmarkFormat(),
    trend_unit: trendUnit,
    trendType: bookmarkParams.trendType ?? TrendChartType.Conversion,
  };
}

export function getBookmarkParamsWithAdditionalFilters(
  bookmarkParams: FunnelBookmark,
  additionalFilters: PropertyFilter[],
): FunnelBookmark {
  const mergedFilters = getMergedBookmarkEventAndCohortFilters(
    {
      filter_by_event: bookmarkParams.filter_by_event,
      filter_by_cohort: bookmarkParams.filter_by_cohort ? JSON.parse(bookmarkParams.filter_by_cohort) : {},
    },
    additionalFilters,
  );

  return {
    ...bookmarkParams,
    filter_by_event: mergedFilters.filter_by_event,
    filter_by_cohort: JSON.stringify(mergedFilters.filter_by_cohort ?? {}),
  };
}

// Some old bookmarks will have missing or deprecated fields, this method will try to apply defaults and correct all fields at once
/** @deprecated - TODO: this should eventually be phased out, you should use a standard util to extract and polyfill only the fields you need
 * Example 1: const myField = FunnelsQuery.fromBookmark(bookmarkParams).getMyField()
 * Or do Example 2: const myField = getMyField(bookmarkParams)
 **/
export function getBookmarkWithAppliedDefaults(bookmarkParams: FunnelBookmark) {
  return FunnelsQuery.fromBookmark(bookmarkParams).generateBookmark();
}
